import fs from "node:fs";
import { callTool, isError } from "../lib/mcp-client.js";

/**
 * `clawnify execute [file]` / `clawnify execute -c "code"` — run
 * TypeScript on the active org's substrate via mcp.clawnify.com.
 *
 * Streams the result to stdout/stderr matching the substrate's exit
 * code. Useful for ad-hoc code-mode invocations from the terminal;
 * agent runtimes call the underlying MCP tool directly.
 */
export async function executeCommand(
  file: string | undefined,
  options: { code?: string; timeout?: string },
) {
  let code: string;
  if (options.code) {
    code = options.code;
  } else if (file) {
    try {
      code = fs.readFileSync(file, "utf-8");
    } catch (err) {
      process.stderr.write(`Failed to read ${file}: ${String(err)}\n`);
      process.exit(1);
    }
  } else {
    process.stderr.write(
      "Provide a file path or --code 'inline code'.\n" +
        "Usage: clawnify execute path/to/file.ts\n" +
        "       clawnify execute -c \"console.log(1 + 1)\"\n",
    );
    process.exit(1);
  }

  const timeout_ms = options.timeout ? Math.max(1000, parseInt(options.timeout, 10) * 1000) : 30000;

  const resp = await callTool("clawnify_execute", { code, timeout_ms });
  if (isError(resp)) {
    process.stderr.write(`Error ${resp.error.code}: ${resp.error.message}\n`);
    process.exit(1);
  }

  const sc = resp.result.structuredContent as
    | {
        stdout?: string;
        stderr?: string;
        exit_code?: number;
        duration_ms?: number;
        timed_out?: boolean;
        substrate_id?: string;
        runtime?: string;
      }
    | undefined;

  if (sc) {
    if (sc.stdout) process.stdout.write(sc.stdout);
    if (sc.stderr) process.stderr.write(sc.stderr);
    if (sc.timed_out) {
      process.stderr.write(`\n[clawnify] timed out after ${timeout_ms}ms on ${sc.substrate_id}\n`);
    }
    process.exit(sc.exit_code ?? 0);
  }

  // Fallback: surface the human-readable content[] entries
  for (const c of resp.result.content) {
    if (c.type === "text") process.stdout.write(c.text + "\n");
  }
  process.exit(resp.result.isError ? 1 : 0);
}
